import { matchPath } from 'react-router-dom';

export const APP_NAME = 'SteerFi';

const SEPARATOR = ' — ';

/** Browsers truncate tab labels hard; keep a record name from crowding out the section. */
const MAX_DETAIL_LENGTH = 60;

/**
 * Route pattern paired with the name of the page it renders, ordered
 * most-specific-first.
 *
 * `matchPath` answers one pattern at a time and has no ranking of its own, so
 * `/prospect-module/current-state` has to be asked before
 * `/prospect-module/:companyId` -- otherwise the static page reads as a company
 * id, which React Router's own ranking would never allow.
 *
 * Names follow the visible `<h1>` rather than the URL, so the title a screen
 * reader reads back matches the heading on screen.
 */
const ROUTE_TITLES: ReadonlyArray<readonly [pattern: string, name: string]> = [
  // Public
  ['/register/business', 'Register a Business'],
  ['/register/individual', 'Register as an Individual'],
  ['/register', 'Register'],
  ['/registration-success', 'Registration Successful'],
  ['/terms-of-use', 'Terms of Use'],
  ['/unauthorized', 'Access Denied'],
  // Protected
  ['/dashboard', 'Dashboard'],
  ['/clients/:id', 'Clients'],
  ['/clients', 'Clients'],
  ['/escrow', 'Escrow'],
  ['/payments/:id', 'Payments'],
  ['/payments', 'Payments'],
  ['/payment-files/:id', 'Payment Files'],
  ['/payment-files', 'Payment Files'],
  ['/suppliers/:id', 'Suppliers'],
  ['/suppliers', 'Suppliers'],
  ['/reconciliation', 'Reconciliation'],
  ['/reports', 'Reports'],
  ['/users', 'Users'],
  ['/providers/:partyId', 'Providers'],
  ['/providers', 'Providers'],
  ['/partners/:partyId', 'Partners'],
  ['/partners', 'Partners'],
  ['/file-formats/:id', 'File Formats'],
  ['/file-formats', 'File Formats'],
  ['/reference-admin', 'Reference Admin'],
  ['/prospect-companies', 'Opportunities'],
  ['/prospect-module/current-state', 'Current State Analysis'],
  ['/prospect-module/:companyId', 'Prospect Module'],
  ['/prospect-module', 'Prospect Module'],
  ['/settings', 'Settings'],
  ['/feature-flags', 'Feature Flags'],
];

/**
 * Name of the page a pathname renders, or `null` when no route claims it.
 *
 * `null` covers two cases that want the same brand-only title: `/`, the
 * landing and sign-in page, which has no section to name; and any unrouted URL,
 * since the app declares no catch-all route.
 */
export function resolveRouteTitle(pathname: string): string | null {
  for (const [pattern, name] of ROUTE_TITLES) {
    // `end: true` is already the default, but spelling it out guards the table:
    // with `end: false` the first pattern would match every path beneath it.
    if (matchPath({ path: pattern, end: true }, pathname)) return name;
  }
  return null;
}

/** Collapses whitespace and caps length so a record name stays readable in a tab. */
export function normalizeDetail(detail: string | null | undefined): string | null {
  const collapsed = detail?.replace(/\s+/g, ' ').trim();
  if (!collapsed) return null;
  return collapsed.length > MAX_DETAIL_LENGTH
    ? `${collapsed.slice(0, MAX_DETAIL_LENGTH - 1)}…`
    : collapsed;
}

/**
 * Builds the `document.title` for a route, e.g. `Acme Corp — Clients — SteerFi`
 * on a detail page and `Clients — SteerFi` on the list it came from.
 */
export function formatDocumentTitle(section: string | null, detail?: string | null): string {
  return [normalizeDetail(detail), section, APP_NAME].filter(Boolean).join(SEPARATOR);
}
